import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from './index.module.css';
import { useService } from '../../../../services/services';
import { useQuery } from '../../../../hooks/use-query';
import Spinner from '../../../../components/common/spinner';
import AnimalEventList from './animal-event-list';
import { useHistory } from 'react-router-dom';
import AnimalEventsCalendar from '../events';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { toggleAnimalCalendarOpen } from '../../../../store/global-slice';
import { useDispatch } from 'react-redux';

interface Props {
  setMissedCountState: Dispatch<SetStateAction<number>>;
  eventsCount: number;
}
export default function AnimalEvents({
  setMissedCountState,
  eventsCount,
}: Props) {
  const { t } = useTranslation();
  const { farmId } = useParams<{ farmId: string }>();
  const [params] = useQuery();
  const { animalEventService } = useService();
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const currentYear = new Date().getFullYear();
  const defaultFrom = `${currentYear}-01-01`;
  const defaultTo = new Date().toISOString().split('T')[0];
  const from = params?.from || defaultFrom;
  const to = params?.to || defaultTo;
  const { isAnimalCalendarOpen } = useSelector((state: RootState) => ({
    isAnimalCalendarOpen: state.global.isAnimalCalendarOpen,
  }));
  const dispatch = useDispatch();
  const toggleCalendarOpen = () => dispatch(toggleAnimalCalendarOpen());

  useEffect(() => {
    if (!isAnimalCalendarOpen && (!params?.from || !params?.to)) {
      const search = new URLSearchParams(params);
      if (!params?.from) search.set('from', defaultFrom);
      if (!params?.to) search.set('to', defaultTo);

      history.push({ ...history.location, search: search.toString() });
    }
  }, [params, defaultFrom, defaultTo, history, isAnimalCalendarOpen]);

  useEffect(() => {
    setLoading(true);
    if (from && to) {
      const subscription = animalEventService
        .syncAnimals(farmId, {
          date_from: from,
          date_to: to,
          sub_events: params?.sub_event,
        })
        .subscribe(() => {
          setLoading(false);
        });

      return () => subscription.unsubscribe();
    }
  }, [animalEventService, farmId, from, to, params?.sub_event]);

  useEffect(() => {
    const year =
      !params?.year || params?.year === ''
        ? new Date().getFullYear()
        : params?.year;
    const month =
      !params?.month || params?.month === ''
        ? String(new Date().getMonth() + 1).padStart(2, '0')
        : params?.month;
    const statuses =
      !params?.statuses || params?.statuses === '' ? '' : params?.statuses;

    const eventsSubscription = animalEventService
      .indexCalendar(farmId, {
        statuses: statuses,
        year: year,
        month: month,
      })
      .subscribe((res) => {
        setMissedCountState(res.missed_count);
      });

    return () => eventsSubscription.unsubscribe();
  }, [
    animalEventService,
    farmId,
    params?.month,
    params?.statuses,
    params?.year,
    setMissedCountState,
  ]);

  if (loading) {
    return (
      <div className={css['loading']}>
        <div>{t('Loading')}</div>
        <Spinner visible={true} size="lg" />
      </div>
    );
  } else {
    return isAnimalCalendarOpen ? (
      <AnimalEventsCalendar
        useCalendarView={isAnimalCalendarOpen}
        setUseCalendarView={toggleCalendarOpen}
        setMissedCountState={setMissedCountState}
      />
    ) : (
      <AnimalEventList
        useCalendarView={isAnimalCalendarOpen}
        setUseCalendarView={toggleCalendarOpen}
        eventsCount={eventsCount}
      />
    );
  }
}
